'use client';

import React, { useState, useEffect } from 'react';
import CaseStudyCard from './CaseStudyCard';
import FilterBar from './FilterBar';
import MetricsDashboard from './MetricsDashboard';
import TestimonialCarousel from './TestimonialCarousel';
import IndustryRecognition from './IndustryRecognition';
import Icon from '@/components/ui/AppIcon';

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  solutionType: string;
  businessSize: string;
  image: string;
  alt: string;
  results: {
    metric: string;
    value: string;
    icon: string;
  }[];
  timeline: string;
  tags: string[];
}

interface FilterOption {
  label: string;
  value: string;
}

interface MetricItem {
  label: string;
  value: string;
  icon: string;
  color: string;
}

interface Testimonial {
  id: string;
  quote: string;
  author: string;
  position: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
  videoUrl?: string;
}

interface Recognition {
  type: 'certification' | 'partnership' | 'media';
  title: string;
  organization: string;
  logo: string;
  alt: string;
  date: string;
  description: string;
}

const CaseStudiesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedSolution, setSelectedSolution] = useState('all');
  const [selectedBusinessSize, setSelectedBusinessSize] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCaseStudies, setFilteredCaseStudies] = useState<CaseStudy[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const caseStudies: CaseStudy[] = [
  {
    id: '1',
    title: 'Klantenservice revolutie met AI Chatbot',
    client: 'TechRetail BV',
    industry: 'E-commerce',
    challenge: 'Hoge volumes aan klantvragen leidden tot lange wachttijden en hoge personeelskosten. Klanttevredenheid daalde met 15% in 6 maanden.',
    solution: 'Implementatie van een geavanceerde AI-chatbot met natuurlijke taalverwerking, geïntegreerd met bestaande CRM en voorraadsystemen.',
    solutionType: 'AI Chatbot',
    businessSize: 'MKB',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13913910e-1764639852428.png",
    alt: 'Modern customer service center with multiple computer screens showing chat interfaces and analytics dashboards',
    results: [
    { metric: 'Kostenreductie', value: '67%', icon: 'CurrencyEuroIcon' },
    { metric: 'Responstijd', value: '-85%', icon: 'ClockIcon' },
    { metric: 'Klanttevredenheid', value: '+42%', icon: 'FaceSmileIcon' },
    { metric: 'Afgehandelde vragen', value: '24/7', icon: 'ChatBubbleLeftRightIcon' }],

    timeline: '3 maanden',
    tags: ['AI Chatbot', 'Klantenservice', 'NLP']
  },
  {
    id: '2',
    title: 'Voorspellende voorraadoptimalisatie',
    client: 'FreshFood Distributie',
    industry: 'Logistiek',
    challenge: 'Hoge voedselverspilling door onnauwkeurige voorspellingen en inefficiënte voorraadplanning. Jaarlijkse verliezen van €450.000.',
    solution: 'Machine learning model voor vraagvoorspelling gecombineerd met geautomatiseerd bestelproces en real-time voorraadmonitoring.',
    solutionType: 'Voorspellende Analytics',
    businessSize: 'MKB',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16c8442d4-1768280986944.png",
    alt: 'Warehouse interior with organized shelves of fresh produce and digital inventory management screens',
    results: [
    { metric: 'Verspilling', value: '-73%', icon: 'ArrowTrendingDownIcon' },
    { metric: 'Voorraadkosten', value: '-45%', icon: 'CurrencyEuroIcon' },
    { metric: 'Voorspellingsnauwkeurigheid', value: '94%', icon: 'ChartBarIcon' },
    { metric: 'ROI', value: '340%', icon: 'ArrowTrendingUpIcon' }],

    timeline: '4 maanden',
    tags: ['Machine Learning', 'Voorraad', 'Logistiek']
  },
  {
    id: '3',
    title: 'Geautomatiseerde social media campagnes',
    client: 'StyleHub Fashion',
    industry: 'Retail',
    challenge: 'Tijdrovend handmatig beheer van social media met inconsistente posting en lage engagement rates. Marketing team overbelast.',
    solution: 'AI-gedreven content generator met automatische planning, A/B testing en performance optimalisatie voor Instagram, Facebook en TikTok.',
    solutionType: 'Social Media AI',
    businessSize: 'Klein',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f0bbdb92-1772817376883.png",
    alt: 'Fashion boutique interior with stylish clothing displays and social media content being created on tablets',
    results: [
    { metric: 'Engagement', value: '+156%', icon: 'HeartIcon' },
    { metric: 'Tijdsbesparing', value: '18u/week', icon: 'ClockIcon' },
    { metric: 'Bereik', value: '+89%', icon: 'UserGroupIcon' },
    { metric: 'Conversies', value: '+64%', icon: 'ShoppingCartIcon' }],

    timeline: '2 maanden',
    tags: ['Social Media', 'Content AI', 'Marketing']
  },
  {
    id: '4',
    title: 'Intelligente documentverwerking',
    client: 'LegalPro Advocaten',
    industry: 'Juridisch',
    challenge: 'Handmatige verwerking van juridische documenten kostte 40% van de werktijd. Hoog risico op menselijke fouten bij contractanalyse.',
    solution: 'AI-systeem voor automatische documentclassificatie, data-extractie en contractanalyse met compliance checking.',
    solutionType: 'Document AI',
    businessSize: 'MKB',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_197b1e7ce-1772483646577.png",
    alt: 'Modern law office with organized legal documents and digital document management system on computer screens',
    results: [
    { metric: 'Verwerkingstijd', value: '-78%', icon: 'ClockIcon' },
    { metric: 'Nauwkeurigheid', value: '99.2%', icon: 'CheckCircleIcon' },
    { metric: 'Capaciteit', value: '+120%', icon: 'ArrowTrendingUpIcon' },
    { metric: 'Kostenreductie', value: '€85k/jaar', icon: 'CurrencyEuroIcon' }],

    timeline: '5 maanden',
    tags: ['Document AI', 'Juridisch', 'Automatisering']
  },
  {
    id: '5',
    title: 'Persoonlijke klantaanbevelingen',
    client: 'BookWorld Online',
    industry: 'E-commerce',
    challenge: 'Lage conversieratio en beperkte cross-selling. Klanten vonden moeilijk relevante producten in het uitgebreide assortiment.',
    solution: 'Geavanceerd aanbevelingssysteem op basis van koopgedrag, browsehistorie en collaborative filtering met real-time personalisatie.',
    solutionType: 'Aanbevelingssysteem',
    businessSize: 'MKB',
    image: "https://images.unsplash.com/photo-1678917648365-9e1927f12a7b",
    alt: 'Cozy bookstore with customers browsing books and digital recommendation screens showing personalized suggestions',
    results: [
    { metric: 'Conversie', value: '+92%', icon: 'ShoppingCartIcon' },
    { metric: 'Gemiddelde orderwaarde', value: '+58%', icon: 'CurrencyEuroIcon' },
    { metric: 'Klantretentie', value: '+47%', icon: 'UserGroupIcon' },
    { metric: 'Cross-selling', value: '+134%', icon: 'ArrowTrendingUpIcon' }],

    timeline: '3 maanden',
    tags: ['Personalisatie', 'E-commerce', 'ML']
  },
  {
    id: '6',
    title: 'Predictief onderhoud voor machines',
    client: 'IndustrieMax Productie',
    industry: 'Productie',
    challenge: 'Ongeplande machine-uitval veroorzaakte productiestoringen en hoge reparatiekosten. Gemiddeld 12 dagen downtime per jaar.',
    solution: 'IoT-sensoren gecombineerd met machine learning voor voorspelling van onderhoudsbehoefte en automatische planning.',
    solutionType: 'Predictief Onderhoud',
    businessSize: 'MKB',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1836d87bc-1764648555662.png",
    alt: 'Industrial factory floor with modern machinery equipped with IoT sensors and maintenance monitoring screens',
    results: [
    { metric: 'Downtime', value: '-81%', icon: 'ArrowTrendingDownIcon' },
    { metric: 'Onderhoudskosten', value: '-56%', icon: 'CurrencyEuroIcon' },
    { metric: 'Productiviteit', value: '+38%', icon: 'ArrowTrendingUpIcon' },
    { metric: 'Levensduur machines', value: '+25%', icon: 'WrenchScrewdriverIcon' }],

    timeline: '6 maanden',
    tags: ['IoT', 'Predictief', 'Productie']
  }];


  const industries: FilterOption[] = [
  { label: 'Alle industrieën', value: 'all' },
  { label: 'E-commerce', value: 'E-commerce' },
  { label: 'Logistiek', value: 'Logistiek' },
  { label: 'Retail', value: 'Retail' },
  { label: 'Juridisch', value: 'Juridisch' },
  { label: 'Productie', value: 'Productie' }];


  const solutions: FilterOption[] = [
  { label: 'Alle oplossingen', value: 'all' },
  { label: 'AI Chatbot', value: 'AI Chatbot' },
  { label: 'Voorspellende Analytics', value: 'Voorspellende Analytics' },
  { label: 'Social Media AI', value: 'Social Media AI' },
  { label: 'Document AI', value: 'Document AI' },
  { label: 'Aanbevelingssysteem', value: 'Aanbevelingssysteem' },
  { label: 'Predictief Onderhoud', value: 'Predictief Onderhoud' }];


  const businessSizes: FilterOption[] = [
  { label: 'Alle groottes', value: 'all' },
  { label: 'Klein (1-50)', value: 'Klein' },
  { label: 'MKB (51-250)', value: 'MKB' }];


  const metrics: MetricItem[] = [
  { label: 'Totale kostenbesparingen', value: '€2.4M+', icon: 'CurrencyEuroIcon', color: 'text-primary' },
  { label: 'Gemiddelde efficiency', value: '+127%', icon: 'ArrowTrendingUpIcon', color: 'text-success' },
  { label: 'Tevreden klanten', value: '98%', icon: 'FaceSmileIcon', color: 'text-accent' },
  { label: 'Projecten afgerond', value: '150+', icon: 'CheckCircleIcon', color: 'text-secondary' }];


  const testimonials: Testimonial[] = [
  {
    id: '1',
    quote: 'De AI-chatbot van AIM Agency heeft onze klantenservice volledig getransformeerd. We behandelen nu 3x meer vragen met hetzelfde team, en onze klanten zijn tevredener dan ooit.',
    author: 'Mark van der Berg',
    position: 'CEO',
    company: 'TechRetail BV',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17f023c94-1763292242416.png",
    alt: 'Professional headshot of middle-aged Dutch businessman in navy suit with confident smile',
    rating: 5,
    videoUrl: 'https://example.com/testimonial-1'
  },
  {
    id: '2',
    quote: 'Dankzij het voorspellende model van AIM hebben we onze voedselverspilling met 73% verminderd. De ROI was binnen 4 maanden bereikt. Ongelooflijk resultaat!',
    author: 'Sophie Janssen',
    position: 'Operations Director',
    company: 'FreshFood Distributie',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1456eb2f9-1763294356174.png",
    alt: 'Professional portrait of young female executive with blonde hair in business attire',
    rating: 5
  },
  {
    id: '3',
    quote: 'Het document AI-systeem heeft ons kantoor gerevolutioneerd. We kunnen nu complexe contracten in minuten analyseren in plaats van uren. Onze productiviteit is enorm gestegen.',
    author: 'Robert de Vries',
    position: 'Managing Partner',
    company: 'LegalPro Advocaten',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1467f0777-1772102223596.png",
    alt: 'Professional headshot of senior male lawyer in dark suit with gray hair',
    rating: 5,
    videoUrl: 'https://example.com/testimonial-3'
  }];


  const recognitions: Recognition[] = [
  {
    type: 'certification',
    title: 'ISO 27001 Gecertificeerd',
    organization: 'International Organization for Standardization',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17a998b41-1764664878567.png",
    alt: 'ISO 27001 certification badge with blue and white security shield emblem',
    date: 'Jan 2025',
    description: 'Erkend voor hoogste standaarden in informatiebeveiliging en data protection voor AI-implementaties.'
  },
  {
    type: 'partnership',
    title: 'Microsoft AI Partner',
    organization: 'Microsoft Corporation',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c2462849-1773246398936.png",
    alt: 'Microsoft partner logo with blue square and white text on professional background',
    date: 'Nov 2024',
    description: 'Officiële Microsoft partner voor Azure AI en Cognitive Services implementaties in de Benelux.'
  },
  {
    type: 'media',
    title: 'Top 10 AI Consultancy Benelux',
    organization: 'TechWeek Magazine',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_139d51ac0-1769260713436.png",
    alt: 'TechWeek magazine cover featuring technology innovation and AI trends',
    date: 'Dec 2024',
    description: 'Erkend als een van de meest innovatieve AI-consultancy bedrijven in de Benelux regio.'
  },
  {
    type: 'certification',
    title: 'GDPR Compliance Expert',
    organization: 'European Data Protection Board',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef5016ca-1772184387597.png",
    alt: 'GDPR compliance certification badge with EU stars and data protection symbols',
    date: 'Okt 2024',
    description: 'Gecertificeerd expert in GDPR-compliant AI-implementaties en data governance.'
  },
  {
    type: 'partnership',
    title: 'Google Cloud AI Partner',
    organization: 'Google Cloud',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_178bf17fa-1767020947152.png",
    alt: 'Google Cloud partner logo with colorful G symbol on white background',
    date: 'Sep 2024',
    description: 'Erkende partner voor Google Cloud AI en Machine Learning Platform implementaties.'
  },
  {
    type: 'media',
    title: 'Innovation Award 2024',
    organization: 'Dutch Tech Awards',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f1ae1a20-1767099880303.png",
    alt: 'Golden trophy award for technology innovation with modern geometric design',
    date: 'Jun 2024',
    description: 'Winnaar van de Innovation Award voor meest impactvolle AI-implementatie in MKB sector.'
  }];


  useEffect(() => {
    if (!isHydrated) return;

    let filtered = caseStudies;

    if (selectedIndustry !== 'all') {
      filtered = filtered.filter((cs) => cs.industry === selectedIndustry);
    }

    if (selectedSolution !== 'all') {
      filtered = filtered.filter((cs) => cs.solutionType === selectedSolution);
    }

    if (selectedBusinessSize !== 'all') {
      filtered = filtered.filter((cs) => cs.businessSize === selectedBusinessSize);
    }

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((cs) =>
      cs.title.toLowerCase().includes(query) ||
      cs.client.toLowerCase().includes(query) ||
      cs.challenge.toLowerCase().includes(query) ||
      cs.solution.toLowerCase().includes(query) ||
      cs.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    setFilteredCaseStudies(filtered);
  }, [isHydrated, selectedIndustry, selectedSolution, selectedBusinessSize, searchQuery]);

  const handleClearFilters = () => {
    setSelectedIndustry('all');
    setSelectedSolution('all');
    setSelectedBusinessSize('all');
    setSearchQuery('');
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-lg w-1/3"></div>
            <div className="h-64 bg-muted rounded-xl"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) =>
              <div key={i} className="h-96 bg-muted rounded-xl"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-16">
      <section>
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full mb-6">
            <Icon name="SparklesIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Bewezen Resultaten</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-display font-bold text-foreground mb-6">
            Case Studies
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Ontdek hoe we Benelux MKB-bedrijven transformeren met praktische AI-oplossingen die directe resultaten opleveren.
          </p>
        </div>

        <MetricsDashboard metrics={metrics} />
      </section>

      <section>
        <FilterBar
          industries={industries}
          solutions={solutions}
          businessSizes={businessSizes}
          selectedIndustry={selectedIndustry}
          selectedSolution={selectedSolution}
          selectedBusinessSize={selectedBusinessSize}
          searchQuery={searchQuery}
          onIndustryChange={setSelectedIndustry}
          onSolutionChange={setSelectedSolution}
          onBusinessSizeChange={setSelectedBusinessSize}
          onSearchChange={setSearchQuery}
          onClearFilters={handleClearFilters} />

      </section>

      <section>
        {filteredCaseStudies.length > 0 ?
        <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-display font-bold text-foreground">
                {filteredCaseStudies.length} {filteredCaseStudies.length === 1 ? 'Case Study' : 'Case Studies'}
              </h2>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="CheckCircleIcon" size={16} className="text-success" />
                <span>Alle resultaten geverifieerd</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCaseStudies.map((caseStudy) =>
            <CaseStudyCard key={caseStudy.id} {...caseStudy} />
            )}
            </div>
          </> :

        <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
              <Icon name="MagnifyingGlassIcon" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-2xl font-display font-bold text-foreground mb-4">
              Geen case studies gevonden
            </h3>
            <p className="text-lg text-muted-foreground mb-8">
              Probeer andere filters of zoektermen om relevante case studies te vinden.
            </p>
            <button
            onClick={handleClearFilters}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all duration-300 hover:shadow-premium">

              Wis alle filters
            </button>
          </div>
        }
      </section>

      <section>
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
            Wat onze klanten zeggen
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Directe feedback van bedrijfsleiders die AI-transformatie hebben ervaren met AIM Agency.
          </p>
        </div>
        <TestimonialCarousel testimonials={testimonials} />
      </section>

      <section>
        <IndustryRecognition recognitions={recognitions} />
      </section>

      <section className="bg-gradient-to-br from-primary/10 via-transparent to-accent/10 border border-primary/20 rounded-2xl p-8 lg:p-12 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-6">
            Klaar voor uw eigen succesverhaal?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Laat ons u laten zien hoe AI uw bedrijf kan transformeren met meetbare resultaten in maanden, niet jaren.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/contact"
              className="w-full sm:w-auto px-8 py-4 bg-cta text-cta-foreground rounded-lg font-semibold text-lg hover:bg-cta/90 transition-all duration-300 hover:shadow-elevated hover:-translate-y-0.5 flex items-center justify-center space-x-2">

              <span>Plan gratis AI-scan</span>
              <Icon name="ArrowRightIcon" size={20} />
            </a>
            <a
              href="/pricing"
              className="w-full sm:w-auto px-8 py-4 bg-transparent border-2 border-primary text-primary rounded-lg font-semibold text-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center space-x-2">

              <span>Bekijk pakketten</span>
              <Icon name="CurrencyEuroIcon" size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>);

};

export default CaseStudiesInteractive;